import { authService } from './auth.service';

const ETL_URL = import.meta.env.VITE_API_URL_ETL || 'http://localhost:8788';

export interface LegacyRow {
  FOLIO: string;
  "FECHA del Documento": string;
  MES: string | number;
  CONCEPTO: string;
  COMENTARIOS: string;
  TIPO: string;
  "EXTRAER INICIAL DE TIPO": string;
  MOVIMIENTO: string;
  year: string | number;
  ID: string;
  "INPUT DATE": string;
  "INPUT TIME": string;
  monto?: string | number;
}

export type LegacyTarget = 'expenses' | 'internal_movements' | 'payroll_cycles';

export interface NormalizedPreviewRow {
  external_id: string;
  entry_date: string;
  concept: string;
  category_name: string;
  department_name: string | null;
  amount: number;
  folio_origin: string;
  source_folio_type: string;
  movement_code: string | null;
  target: LegacyTarget;
  anomaly_flags: string[];
}

export interface PreviewResponse {
  total: number;
  valid: number;
  normalizeErrors: { row: number; message: string }[];
  distribution: {
    expenses: number;
    internal_movements: number;
    payroll_cycles: number;
  };
  anomalies: Record<string, number>;
  preview: {
    expenses: NormalizedPreviewRow[];
    internal_movements: NormalizedPreviewRow[];
    payroll_cycles: NormalizedPreviewRow[];
  };
}

export interface ImportResponse {
  success: boolean;
  imported: number;
  failed: number;
  errors: { row: number; message: string }[];
  summary: {
    expenses: number;
    internalMovements: number;
    payrollCycles: number;
    skipped: number;
  };
  normalizationErrors: { row: number; message: string }[];
  timestamp: string;
}

export const legacyImportService = {
  /**
   * Envía las filas legacy al backend para previsualización.
   * NO escribe nada en la base de datos.
   */
  async preview(rows: LegacyRow[]): Promise<PreviewResponse> {
    const response = await authService.fetchWithAuth(
      `${ETL_URL}/api/etl/preview-legacy-expenses`,
      {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Error al generar preview');
    }

    const result = await response.json();
    return result.data as PreviewResponse;
  },

  /**
   * Ejecuta la importación real.
   * El backend segmenta automáticamente por target.
   */
  async import(rows: LegacyRow[]): Promise<ImportResponse> {
    const response = await authService.fetchWithAuth(
      `${ETL_URL}/api/etl/import-legacy-expenses`,
      {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Error al importar');
    }

    const result = await response.json();
    return {
      ...result.data,
      normalizationErrors: result.data?.normalizationErrors || [],
    } as ImportResponse;
  },


  async checkExisting(externalIds: string[]): Promise<Set<string>> {
    if (!externalIds || externalIds.length === 0) return new Set();

    const existing = new Set<string>();
    const CHUNK = 500;

    for (let i = 0; i < externalIds.length; i += CHUNK) {
        const chunk = externalIds.slice(i, i + CHUNK);

        const response = await authService.fetchWithAuth(
        `${ETL_URL}/api/etl/check-existing`,
        {
            method: "POST",
            body: JSON.stringify({ external_ids: chunk }),
        }
        );

        if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Error al verificar registros existentes");
        }

        const result = await response.json();
        const ids: string[] = result.data?.existing || [];
        for (const id of ids) existing.add(id);
    }

    return existing;
    },

};